import { IconHelp } from '@tabler/icons-react';
import { calculateAgeForEnrollment } from '@zorro/common-utils';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { Grid, Icon, Stack, Text } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { ReactNode } from 'react';

import {
  getEnrollmentDateInfo,
  getEnrollmentOverviewQleDetails,
} from './EnrollmentOverviewCardUtils';

type InfoItemProps = {
  label: string;
  value: ReactNode;
  isHighlighted?: boolean;
};

const EnrollmentOverviewInfoItem = ({
  label,
  value,
  isHighlighted = false,
}: InfoItemProps) => {
  if (!value) {
    return null;
  }

  return (
    <>
      <Grid.Col>
        <Text
          size={isHighlighted ? 'md' : 'sm'}
          c="var(--color-text-default-secondary)"
        >
          {label}
        </Text>
      </Grid.Col>
      <Grid.Col>
        <Text size={isHighlighted ? 'md' : 'sm'} fw={isHighlighted ? 600 : 400}>
          {value}
        </Text>
      </Grid.Col>
    </>
  );
};

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
};

export const EnrollmentOverviewCard = ({
  employeeId,
  onboardingPeriodId,
}: Props) => {
  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: [onboardingPeriodId],
  });

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  if (!onboardingPeriod || !employee) {
    return null;
  }

  const ageAtPlanStart = calculateAgeForEnrollment(
    employee.dateOfBirth,
    onboardingPeriod.effectiveFrom
  );
  const qleDetails = getEnrollmentOverviewQleDetails(onboardingPeriod);
  const { label: dateLabel, value: dateValue } =
    getEnrollmentDateInfo(onboardingPeriod);

  return (
    <Grid
      bg="var(--color-background-general-note-primary)"
      gap={0}
      bdrs="sm"
      p="lg"
    >
      <EnrollmentOverviewInfoItem
        label={dateLabel}
        value={dateValue}
        isHighlighted
      />
      <EnrollmentOverviewInfoItem
        label="Enrollment team"
        value={onboardingPeriod.enrollmentTeamName}
      />
      <EnrollmentOverviewInfoItem
        label="Enrollment agent"
        value={onboardingPeriod.enrollmentAgentName}
      />
      <EnrollmentOverviewInfoItem
        label="Class"
        value={onboardingPeriod.class}
      />
      <EnrollmentOverviewInfoItem
        label="Age at plan start"
        value={String(ageAtPlanStart)}
      />
      <EnrollmentOverviewInfoItem
        label="QLE details"
        value={
          qleDetails && (
            <>
              <Text mr="xxs">{qleDetails.label}</Text>
              {qleDetails.tooltip && (
                <Tooltip
                  label={
                    <Stack gap={0}>
                      <Text fw={600} size="sm">
                        Employee's description:
                      </Text>
                      <Text size="sm">{qleDetails.tooltip}</Text>
                    </Stack>
                  }
                >
                  <Icon
                    size="1.25rem"
                    icon={IconHelp}
                    style={{
                      verticalAlign: 'top',
                    }}
                  />
                </Tooltip>
              )}
              <Text>, {qleDetails.date}</Text>
            </>
          )
        }
      />
    </Grid>
  );
};
